import LocalStorage from "../utils/LocalStorage";

// Detectar automáticamente la URL base de la API según el entorno
const getApiBaseUrl = () => {
    // Prioridad 1: variable de entorno explícita (bakeada en el build de producción)
    if (import.meta.env.VITE_API_URL) {
        return import.meta.env.VITE_API_URL;
    }

    const isPrivateIP = window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.match(/^192\.168\./) ||
        window.location.hostname.match(/^10\./) ||
        window.location.hostname.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./);

    if (!isPrivateIP) {
        // Producción con dominio público: API en el mismo dominio en /api
        return `${window.location.protocol}//${window.location.hostname}/api`;
    }

    // Desarrollo local o red privada
    const backendPort = import.meta.env.VITE_BACKEND_PORT || '63000';
    return `http://${window.location.hostname}:${backendPort}/api`;
};

const apiBaseUrl = getApiBaseUrl();
console.log('apiBaseUrl (auto-detected):', apiBaseUrl);

/**
 * Helper para obtener headers con autenticación
 * @param {Object} additionalHeaders - Headers adicionales opcionales
 * @returns {Object} Headers con Authorization si existe token
 */
const getAuthHeaders = (additionalHeaders = {}) => {
    const token = localStorage.getItem('authToken');
    const headers = { ...additionalHeaders };
    
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
};

/**
 * fetch autenticado: añade el JWT y detecta expiración de sesión (401)
 */
const fetchWithAuth = async (url, options = {}) => {
    const token = localStorage.getItem('authToken');
    const headers = { ...(options.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
    }

    return response;
};

export class FddApi {
    static async createDraft(file, nextcloudMetadata = null) {
        if (!file) { 
            return {
                'title': 'Error en la petición',
                'message': 'No se ha seleccionado un archivo',
                'hasError': true,
            };
        }
        
        const formData = new FormData();
        formData.append('file', file);
        // Ya no enviamos userId - se obtiene del token JWT en el backend

        // Add Nextcloud metadata if provided
        if (nextcloudMetadata) {
            if (nextcloudMetadata.sourcePath) {
                formData.append('sourcePath', nextcloudMetadata.sourcePath);
            }
            if (nextcloudMetadata.saveOption) {
                formData.append('saveOption', nextcloudMetadata.saveOption);
            }
            if (nextcloudMetadata.destinationPath) {
                formData.append('destinationPath', nextcloudMetadata.destinationPath);
            }
            if (nextcloudMetadata.overwrite !== undefined) {
                formData.append('overwrite', nextcloudMetadata.overwrite.toString());
            }
        }

        try {
            const response = await fetchWithAuth(`${apiBaseUrl}/draft/create`, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (!response.ok) throw new Error("Client: Error en la peticion ", data.message);

            console.log('[CREATE DRAFT] -> response', data);

            return {
                'hasError': false,
                'message': data.message,
                'draftId': data.body.draftId,
            }
            //console.log(data);
        } catch (error) {
            console.log('[CREATE DRAFT] Error en la petición', error);
            return {
                'hasError': true,
                'message': error,
            }
        }
    }

    static async updateDraft (draftId, draft) {
        LocalStorage.clearLocalDraft();

        if (!draftId || !draft) return;
        // const status = isRequestComplete ? 2 : 1;

        try {
            const response = await fetchWithAuth(`${apiBaseUrl}/draft/update/${draftId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    "data": draft,
                })
            });

            if (!response.ok) throw new Error("Client: Error en la peticion");

            const {message} = await response.json();

            console.log('[UPDATE DRAFT] -> response', message);

            return {
                'hasError': false,
                message
            }
        } catch (error) {
            console.log('[UPDATE DRAFT] Error en la petición', error);
            return {
                'hasError': true,
                'message': error,
            }
        }
    }

    static async sendDraft (draftId, clientId, draft) {
        LocalStorage.clearLocalDraft();

        try {
            const response = await fetchWithAuth(`${apiBaseUrl}/draft/send/${draftId}`,{
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body:JSON.stringify({
                    clientId,
                    draft
                })
            })

            const {message} = await response.json();

            if (!response.ok) throw new Error("Client: Error en la peticion ->", message);


            console.log('[SEND DRAFT] -> response', message);
            return {
                hasError: false,
                message
            }
        } catch (error) {
            console.log('[SEND DRAFT] Error en la petición', error);
            return {
                hasError: true,
                message: error
            }
        }
    }

    static async getDraftData (draftId) {
        try {
            const response = await fetchWithAuth(`${apiBaseUrl}/draft/data/${draftId}`,{
                method: 'GET'
            });

            const data = await response.json();
            if (!response.ok) throw new Error("Client: Error en la peticion ", data.message);

            console.log('[GET DRAFT DATA] -> response', data);

            return {
                'hasError': false,
                'message': data.message,
                'body': data.body,
            }
        } catch (error) {
            console.log('[GET DRAFT DATA] Error en la petición', error);
            
            return {
                'hasError': true,
                'message': error,
            }
        }
    }

    static async getSignature (signatureId) {
        try {
            const response = await fetch(`${apiBaseUrl}/sign/data/${signatureId}`,{
                method: 'GET'
            });

            const data = await response.json();
            if (!response.ok) throw new Error("Client: Error en la peticion ", data.message);

            console.log('[GET SIGNATURE DATA] -> response', data);

            return {
                'hasError': false,
                'message': data.message,
                'body': data.body,
                'status': data.status,
            }
        } catch (error) {
            console.log('[GET SIGNATURE DATA] Error en la petición', error);
            
            return {
                'hasError': true,
                'message': error,
            }
        }
    }

    static async getDocument (hash, module) {
        try {
            const response = await fetchWithAuth(`${apiBaseUrl}/${module}/file/${hash}`, {
                method: 'GET'
            });

            if (!response.ok) throw new Error("Client: Error en la peticion");

            const body = await response.arrayBuffer();

            console.log('[GET DOCUMENT] -> response', 'Document fetched');

            return {
                'hasError': false,
                'message': 'Document fetched',
                'body': body,
            }
        } catch (error) {
            console.error('[GET DOCUMENT] Error en la petición', error);

            return {
                'hasError': true,
                'message': error,
            }
        }
    }

    /**
     * Descargar un borrador (PDF) usando el token de autenticación
     * @param {string} draftId
     */
    static async downloadDraft (draftId) {
        try {
            const response = await fetchWithAuth(`${apiBaseUrl}/draft/download/${draftId}`, {
                method: 'GET'
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || 'Error al descargar el archivo');
            }

            const blob = await response.blob();
            const contentDisposition = response.headers.get('Content-Disposition') || '';
            const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
            const filename = filenameMatch ? filenameMatch[1] : `documento-${draftId}.pdf`;

            return { hasError: false, blob, filename };
        } catch (error) {
            console.error('[DOWNLOAD DRAFT] Error:', error);
            return { hasError: true, message: error.message };
        }
    }

    static async completeSignature (signatureId, fieldsByPage) {
        try {
            const response = await fetchWithAuth(`${apiBaseUrl}/sign/complete/${signatureId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fieldsByPage })
            });

            const data = await response.json();
            
            if (!response.ok) throw new Error(data.message || "Client: Error completing signature");

            console.log('[COMPLETE SIGNATURE] -> response', data);

            return {
                'hasError': false,
                'message': data.message,
                'body': data.body,
            }
        } catch (error) {
            console.error('[COMPLETE SIGNATURE] Error en la petición', error);

            return {
                'hasError': true,
                'message': error.message || error,
            }
        }
    }

    static async deleteDraft (draftId) {
        try {
            const response = await fetchWithAuth(`${apiBaseUrl}/draft/delete/${draftId}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Error al eliminar el documento');
            return { hasError: false, message: data.message };
        } catch (error) {
            console.error('[DELETE DRAFT] Error en la petición', error);
            return { hasError: true, message: error.message || error };
        }
    }

    static async getDocuments () {
        try {
            const response = await fetchWithAuth(`${apiBaseUrl}/draft/list`);
            const data = await response.json();
            
            if (!response.ok) throw new Error(data.message || "Client: Error fetching documents");

            console.log('[GET DOCUMENTS] -> response', data);

            return {
                'hasError': false,
                'message': data.message,
                'body': data.body,
            }
        } catch (error) {
            console.error('[GET DOCUMENTS] Error en la petición', error);

            return {
                'hasError': true,
                'message': error.message || error,
                'body': [],
            }
        }
    }

    // ==================== PLANS ====================

    /**
     * Obtener planes disponibles (público, sin autenticación)
     */
    static async getAvailablePlans() {
        try {
            const response = await fetch(`${apiBaseUrl}/plans/available`);
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Error al obtener planes');
            return { hasError: false, plans: data.plans };
        } catch (error) {
            console.error('[GET PLANS] Error:', error);
            return { hasError: true, message: error.message };
        }
    }

    // ==================== ACCOUNT MANAGEMENT ====================

    /**
     * Change user's subscription plan (para plan gratuito o downgrade sin pago)
     * @param {number|string} newPlanId - ID of the new plan to change to
     */
    static async changePlan(newPlanId) {
        try {
            const response = await fetchWithAuth(`${apiBaseUrl}/account/change-plan`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newPlanId })
            });

            const data = await response.json();

            console.log('[CHANGE PLAN] -> response', data);

            return {
                hasError: !response.ok,
                statusCode: response.status,
                ...data
            };
        } catch (error) {
            console.error('[CHANGE PLAN] Error en la petición', error);

            return {
                hasError: true,
                statusCode: 500,
                success: false,
                error: error.message || 'Error al cambiar el plan',
            };
        }
    }

    /**
     * Crear suscripción de pago en PayPal
     * @param {string} planName - Nombre del plan: 'Basic', 'Medium' o 'Business'
     * @returns {Promise<Object>} approvalUrl para redirigir al usuario a PayPal
     */
    static async createSubscription(planName) {
        try {
            const response = await fetchWithAuth(`${apiBaseUrl}/payments/create-subscription`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ planName })
            });

            const data = await response.json();

            console.log('[CREATE SUBSCRIPTION] -> response', data);

            return {
                hasError: !response.ok,
                statusCode: response.status,
                ...data
            };
        } catch (error) {
            console.error('[CREATE SUBSCRIPTION] Error en la petición', error);

            return {
                hasError: true,
                statusCode: 500,
                success: false,
                error: error.message || 'Error al crear suscripción',
            };
        }
    }

    // ==================== OAUTH2 AUTHENTICATION ====================

    /**
     * Initiate OAuth2 login with Nextcloud
     * Returns the authorization URL to redirect the user
     */
    static async initiateOAuth2Login() {
        try {
            const response = await fetch(`${apiBaseUrl}/auth/nextcloud`, {
                method: 'GET',
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to initiate OAuth2 login');
            }

            console.log('[OAUTH2 LOGIN] -> Authorization URL obtained');

            return {
                hasError: false,
                authUrl: data.authUrl,
                state: data.state
            };
        } catch (error) {
            console.error('[OAUTH2 LOGIN] Error initiating login:', error);

            return {
                hasError: true,
                message: error.message || 'Failed to initiate OAuth2 login'
            };
        }
    }

    /**
     * Get current user information
     * Requires JWT token in localStorage
     */
    static async getCurrentUser() {
        try {
            const token = LocalStorage.get('authToken');

            if (!token) {
                return {
                    hasError: true,
                    message: 'No authentication token found'
                };
            }

            const response = await fetchWithAuth(`${apiBaseUrl}/auth/me`, {
                method: 'GET'
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to get user info');
            }

            console.log('[GET CURRENT USER] -> User info:', data.user);

            return {
                hasError: false,
                user: data.user
            };
        } catch (error) {
            console.error('[GET CURRENT USER] Error:', error);

            return {
                hasError: true,
                message: error.message || 'Failed to get user information'
            };
        }
    }

    /**
     * Logout user
     * Removes JWT token and OAuth2 tokens from backend
     */
    static async logout() {
        try {
            const token = LocalStorage.get('authToken');

            if (token) {
                await fetchWithAuth(`${apiBaseUrl}/auth/logout`, {
                    method: 'POST'
                });
            }

            // Remove token from localStorage
            LocalStorage.remove('authToken');

            console.log('[LOGOUT] -> User logged out');

            return {
                hasError: false
            };
        } catch (error) {
            console.error('[LOGOUT] Error:', error);

            // Remove token anyway
            LocalStorage.remove('authToken');

            return {
                hasError: true,
                message: error.message || 'Logout failed'
            };
        }
    }

    // ==================== NEXTCLOUD FILE BROWSER ====================

    /**
     * Browse Nextcloud files
     * @param {string} path - Path to browse (default: '/')
     * @param {boolean} onlyPDFs - Filter only PDF files
     */
    static async browseNextcloudFiles(path = '/', onlyPDFs = false) {
        try {
            const token = LocalStorage.get('authToken');

            if (!token) {
                return {
                    hasError: true,
                    message: 'Authentication required'
                };
            }

            const params = new URLSearchParams({
                path,
                onlyPDFs: onlyPDFs.toString()
            });

            const response = await fetch(`${apiBaseUrl}/nextcloud/browse?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to browse Nextcloud files');
            }

            console.log('[BROWSE NEXTCLOUD] -> Files:', data.items.length);

            return {
                hasError: false,
                path: data.path,
                items: data.items,
                count: data.count
            };
        } catch (error) {
            console.error('[BROWSE NEXTCLOUD] Error:', error);

            return {
                hasError: true,
                message: error.message || 'Failed to browse Nextcloud files'
            };
        }
    }

    /**
     * Download a file from Nextcloud
     * @param {string} path - File path to download
     */
    static async downloadNextcloudFile(path) {
        try {
            const token = LocalStorage.get('authToken');

            if (!token) {
                return {
                    hasError: true,
                    message: 'Authentication required'
                };
            }

            const params = new URLSearchParams({ path });

            const response = await fetch(`${apiBaseUrl}/nextcloud/download?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to download file');
            }

            const blob = await response.blob();
            const filename = path.split('/').pop();

            console.log('[DOWNLOAD NEXTCLOUD] -> File:', filename);

            return {
                hasError: false,
                blob,
                filename
            };
        } catch (error) {
            console.error('[DOWNLOAD NEXTCLOUD] Error:', error);

            return {
                hasError: true,
                message: error.message || 'Failed to download file'
            };
        }
    }

    /**
     * Obtener la firma guardada del usuario autenticado
     */
    static async getSavedSignature() {
        try {
            const response = await fetch(`${apiBaseUrl}/account/saved-signature`, {
                method: 'GET',
                headers: getAuthHeaders(),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Error al obtener firma guardada');
            console.log('[GET SAVED SIGNATURE] ->', data);
            return { hasError: false, savedSignature: data.savedSignature };
        } catch (error) {
            console.error('[GET SAVED SIGNATURE] Error:', error);
            return { hasError: true, message: error.message };
        }
    }

    /**
     * Guardar firma como imagen base64 para el usuario autenticado
     * @param {string} base64 - Imagen base64 de la firma (data URL o base64 puro)
     */
    static async saveSavedSignature(base64) {
        try {
            const response = await fetch(`${apiBaseUrl}/account/saved-signature`, {
                method: 'POST',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ savedSignature: base64 }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Error al guardar firma');
            console.log('[SAVE SIGNATURE] ->', data);
            return { hasError: false, message: data.message };
        } catch (error) {
            console.error('[SAVE SIGNATURE] Error:', error);
            return { hasError: true, message: error.message };
        }
    }

    // ── Recuperación de contraseña ──────────────────────────────────────────

    /**
     * Solicitar enlace de reset de contraseña
     * @param {string} email
     */
    static async forgotPassword(email) {
        try {
            const response = await fetch(`${apiBaseUrl}/session/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al procesar solicitud');
            return { hasError: false, message: data.message };
        } catch (error) {
            console.error('[FORGOT PASSWORD] Error:', error);
            return { hasError: true, message: error.message };
        }
    }

    /**
     * Aplicar nuevo password con token recibido por email
     * @param {string} token - Token de la URL
     * @param {string} newPassword
     */
    static async resetPassword(token, newPassword) {
        try {
            const response = await fetch(`${apiBaseUrl}/session/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, newPassword }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al restablecer contraseña');
            return { hasError: false, message: data.message };
        } catch (error) {
            console.error('[RESET PASSWORD] Error:', error);
            return { hasError: true, message: error.message };
        }
    }

    // ── Admin API ───────────────────────────────────────────────────────────

    static async adminGetStats() {
        try {
            const response = await fetch(`${apiBaseUrl}/admin/stats`, { headers: getAuthHeaders() });
            const data = await response.json();
            if (!response.ok) return { hasError: true, statusCode: response.status, message: data.message };
            return { hasError: false, stats: data.stats };
        } catch (error) {
            return { hasError: true, message: error.message };
        }
    }

    static async adminGetUsers(search = '', limit = 50, offset = 0) {
        try {
            const params = new URLSearchParams({ search, limit, offset });
            const response = await fetch(`${apiBaseUrl}/admin/users?${params}`, { headers: getAuthHeaders() });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al obtener usuarios');
            return { hasError: false, users: data.users, total: data.total };
        } catch (error) {
            return { hasError: true, message: error.message };
        }
    }

    static async adminUpdateUserPlan(userId, planId) {
        try {
            const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/plan`, {
                method: 'PUT',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ planId }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al actualizar plan');
            return { hasError: false, message: data.message };
        } catch (error) {
            return { hasError: true, message: error.message };
        }
    }

    static async adminUpdateUserStatus(userId, status) {
        try {
            const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/status`, {
                method: 'PUT',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ status }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al actualizar estado');
            return { hasError: false, message: data.message };
        } catch (error) {
            return { hasError: true, message: error.message };
        }
    }

    static async adminGetDocuments(search = '', limit = 50, offset = 0) {
        try {
            const params = new URLSearchParams({ search, limit, offset });
            const response = await fetch(`${apiBaseUrl}/admin/documents?${params}`, { headers: getAuthHeaders() });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al obtener documentos');
            return { hasError: false, documents: data.documents, total: data.total };
        } catch (error) {
            return { hasError: true, message: error.message };
        }
    }

    static async adminGetPlans() {
        try {
            const response = await fetch(`${apiBaseUrl}/admin/plans`, { headers: getAuthHeaders() });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al obtener planes');
            return { hasError: false, plans: data.plans };
        } catch (error) {
            return { hasError: true, message: error.message };
        }
    }

    static async adminUpdatePlan(planId, updates) {
        try {
            const response = await fetch(`${apiBaseUrl}/admin/plans/${planId}`, {
                method: 'PUT',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify(updates),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al actualizar plan');
            return { hasError: false, message: data.message };
        } catch (error) {
            return { hasError: true, message: error.message };
        }
    }

    static async adminGetLogs({ file = 'combined', limit = 200, level = '', search = '' } = {}) {
        try {
            const params = new URLSearchParams({ file, limit, level, search });
            const response = await fetch(`${apiBaseUrl}/admin/logs?${params}`, { headers: getAuthHeaders() });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Error al obtener logs');
            return { hasError: false, logs: data.logs, total: data.total, file: data.file };
        } catch (error) {
            return { hasError: true, message: error.message, logs: [] };
        }
    }
}